import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Film, Image as ImageIcon } from "lucide-react";
import { getDramaCoverUrl, getDramasByActor } from "@/services/firebase/drama";
import { castPositionLabel } from "@/lib/cast";
import { AdminEmptyState, AdminErrorAlert, AdminLoadingSkeleton, AdminStatusBadge } from "@/components/admin-ui";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

/**
 * 藝人頁「參演作品」：所有 dramas.actors 含此演員的短劇（含未上架），唯讀，連到短劇編輯頁。
 */
export default function ArtistWorksPanel({ artistId }: { artistId: string }) {
  const works = useQuery({
    queryKey: ["dramas-by-actor", artistId],
    queryFn: () => getDramasByActor(artistId),
    enabled: !!artistId,
  });
  const items = works.data ?? [];
  const released = items.filter((d) => d.isReleased).length;

  return (
    <Card>
      <CardHeader>
        <CardTitle>參演作品{works.data ? `（${items.length}）` : ""}</CardTitle>
        <CardDescription>
          卡司包含此藝人的所有短劇。只有「已上架」的短劇會出現在 App 藝人專頁的作品裡
          {works.data ? `（目前 ${released} 部）` : ""}；卡司順序請到短劇編輯頁調整。
        </CardDescription>
      </CardHeader>
      <CardContent>
        {works.isLoading ? (
          <AdminLoadingSkeleton rows={3} />
        ) : works.isError ? (
          <AdminErrorAlert message="讀取參演作品失敗，請重新整理後再試" />
        ) : items.length === 0 ? (
          <AdminEmptyState
            icon={<Film className="h-6 w-6" />}
            title="尚未連結任何短劇"
            description="在短劇編輯頁的「演員（卡司）」加入此藝人後，作品就會出現在這裡。"
          />
        ) : (
          <ul className="divide-y rounded-md border">
            {items.map((drama) => {
              const index = (drama.actors ?? []).indexOf(artistId);
              const cover = getDramaCoverUrl(drama.drama_cover_url);
              return (
                <li key={drama.id}>
                  <Link
                    to={`/dramas/${drama.id}/edit`}
                    className="flex items-center gap-3 px-3 py-2 hover:bg-muted/50 focus:bg-muted/50 focus:outline-none"
                  >
                    {cover ? (
                      <img
                        src={cover}
                        alt=""
                        width={36}
                        height={48}
                        loading="lazy"
                        decoding="async"
                        className="h-12 w-9 shrink-0 rounded bg-muted object-cover"
                      />
                    ) : (
                      <div className="flex h-12 w-9 shrink-0 items-center justify-center rounded bg-muted">
                        <ImageIcon className="h-4 w-4 text-muted-foreground" aria-hidden />
                      </div>
                    )}
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium">{drama.drama_title || drama.drama_id || drama.id}</span>
                      {index >= 0 && <span className="text-xs text-muted-foreground">{castPositionLabel(index)}</span>}
                    </span>
                    <AdminStatusBadge
                      label={drama.isReleased ? "已上架" : "未上架"}
                      tone={drama.isReleased ? "success" : "neutral"}
                    />
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
